import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { OAuthButtons } from './OAuthButtons';
import { VKIDWidget } from './VKIDWidget';
import { trackEvent } from '../../utils/analytics';

export const RegisterForm: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [localError, setLocalError] = useState('');
  const { register, isLoading, error, message } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setLocalError(t('auth.passwordsNoMatch'));
      trackEvent('register_error', { reason: 'passwords_no_match' });
      return;
    }
    setLocalError('');
    trackEvent('register_submit', { method: 'password' });
    try {
      await register(email, password);
      trackEvent('register_success', { method: 'password' });
    } catch (err: any) {
      trackEvent('register_error', { reason: String(err?.message || err || 'unknown').slice(0, 64) });
    }
  };

  const displayError = localError || error;
  const displayMessage = message ? t(message, { defaultValue: message }) : null;

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <h2 className="auth-form__heading">{t('auth.registerHeading')}</h2>

      {displayMessage && (
        <div className="auth-form__msg auth-form__msg--success">{displayMessage}</div>
      )}
      {displayError && (
        <div className="auth-form__msg auth-form__msg--error">{displayError}</div>
      )}

      <div className="auth-form__field">
        <label className="auth-form__label" htmlFor="email">{t('auth.emailLabel')}</label>
        <input
          className="auth-form__input"
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="user@example.com"
          autoComplete="email"
        />
      </div>

      <div className="auth-form__field">
        <label className="auth-form__label" htmlFor="password">{t('auth.passwordLabel')}</label>
        <input
          className="auth-form__input"
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder={t('auth.passwordPlaceholder')}
          autoComplete="new-password"
        />
      </div>

      <div className="auth-form__field">
        <label className="auth-form__label" htmlFor="confirm">{t('auth.confirmPasswordLabel')}</label>
        <input
          className="auth-form__input"
          id="confirm"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          placeholder={t('auth.confirmPlaceholder')}
          autoComplete="new-password"
        />
      </div>

      <button
        type="submit"
        className="auth-form__submit"
        disabled={isLoading}
      >
        {isLoading ? t('auth.loading') : t('auth.createAccountBtn')}
      </button>

      <OAuthButtons />
      <VKIDWidget />
    </form>
  );
};
